/**
 * Suggests a canonical stage for a raw provider value.
 *
 * A SUGGESTION ONLY. It pre-fills a dropdown; it never writes a mapping row.
 * That distinction is the whole point. The taxonomy schema is explicit that
 * "Prelim Assessment Issued" and "Preliminary Assessment Issued" should be two
 * auditable rows a human chose to point at one key, "not a silent match made by
 * a heuristic that will one day merge two stages that mean different things".
 *
 * So this is allowed to be approximate in a way the MATCHER is not: the cost of
 * a wrong suggestion is one extra click, and the cost of a wrong match is deals
 * silently filed in the wrong stage forever.
 *
 * Kept as a pure module with no imports so it can be tested without dragging a
 * Firebase client into the runner.
 */

export interface CanonicalOption {
  key: string;
  label: string;
}

export interface Suggestion {
  key: string;
  /** 0 to 1. Below WEAK the caller should present it as a guess, not an answer. */
  score: number;
}

/** Confidence below which a suggestion is worth showing but not leaning on. */
export const WEAK_SUGGESTION = 0.55;

/** Words that carry no stage meaning and would otherwise inflate overlap. */
const NOISE = new Set(['the', 'a', 'of', 'to', 'in', 'with', 'and', 'deal', 'deals', 'stage']);

/**
 * Strip the decoration Salestrekker wraps around a stage name.
 *
 * Real values look like "05. Fact Find Completed", "A30. Approved" and
 * "Document Collection (Client Portal Issued)". The numbering is a sort key, the
 * parenthetical is usually a variant or an age flag, and neither changes which
 * stage the deal is in.
 */
export function normaliseForSuggestion(raw: string): string {
  return raw
    .replace(/^\s*[A-Za-z]?\d+[.)]\s*/, '')   // leading "05. " or "A30. "
    .replace(/\([^)]*\)/g, ' ')                 // "(Client Portal Issued)", "(Stale)"
    .replace(/[^A-Za-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

const tokens = (s: string): string[] =>
  normaliseForSuggestion(s).split(' ').filter((t) => t.length > 1 && !NOISE.has(t));

/**
 * Token overlap, biased towards the canonical label being covered.
 *
 * "Prelim Assessment Issued" should score highly against "Preliminary
 * Assessment Issued" on the strength of two shared tokens, without a stemmer
 * that might one day equate words that are not the same.
 */
function score(rawTokens: string[], canonicalTokens: string[]): number {
  if (rawTokens.length === 0 || canonicalTokens.length === 0) return 0;

  let hits = 0;
  for (const c of canonicalTokens) {
    if (rawTokens.some((r) => r === c || r.startsWith(c) || c.startsWith(r))) hits += 1;
  }
  const covered = hits / canonicalTokens.length;
  const precision = hits / rawTokens.length;
  // Coverage of the canonical label matters more than brevity of the raw value:
  // "Document Collection (Portal Issued)" should still match "Document
  // Collection" strongly despite carrying extra words.
  return covered * 0.75 + precision * 0.25;
}

/**
 * Best canonical guesses for one raw value, strongest first.
 *
 * Returns an empty list rather than a weak guess when nothing overlaps at all,
 * because an empty picker invites a decision while a bad default invites a
 * thoughtless confirmation.
 */
export function suggestCanonical(
  raw: string,
  canonical: readonly CanonicalOption[],
  limit = 2,
): Suggestion[] {
  const rawTokens = tokens(raw);
  if (rawTokens.length === 0) return [];

  return canonical
    .map((c) => ({ key: c.key, score: score(rawTokens, tokens(c.label)) }))
    .filter((s) => s.score > 0.3)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

/**
 * Group raw values that differ only by decoration.
 *
 * Salestrekker emits "Document Collection (Portal Issued)", "(Client Portal
 * Issued)" and "(PIP)" for one real stage. Offering them as one action saves
 * three identical decisions, but the group is still CONFIRMED by a human: this
 * returns candidates to present, never a merge to apply.
 */
export function groupByNormalisedForm(raws: readonly string[]): Map<string, string[]> {
  const groups = new Map<string, string[]>();
  for (const raw of raws) {
    const key = normaliseForSuggestion(raw);
    if (key === '') continue;
    groups.set(key, [...(groups.get(key) ?? []), raw]);
  }
  return groups;
}
